import dayjs from "dayjs";
import * as XLSX from 'xlsx';
import { getPreference } from "../../Utils/userPreferenceManger";
import { utcToLocal } from "../../Utils/timeConversions";

export const formatFixTimestamp = (value) => {
  if (!value) return "---";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "---";
  return parsed.format("DDMMMYY HH:mm:ss");
};

// Stable module-scope render for the local-time column – must not be recreated
// on every render or BondTable's useEffects will treat the column as "changed"
// and call setRawColumns mid-drag, unmounting the DragDropContext.
const renderLocalTimestamp = (_value, row) => {
  const local = utcToLocal(row?.time);
  return local ? local.format("DDMMMYY HH:mm:ss") : "---";
};

const BASE_COLUMNS = [
  {
    key: "time",
    name: "Timestamp (UTC)",
    render: (value) => formatFixTimestamp(value),
  },
  {
    key: "time_local",
    name: "Timestamp",
    render: renderLocalTimestamp,
  },
  { key: "sendercompid", name: "Sender CompID" },
  { key: "targetcompid", name: "Target CompID" },
  { key: "text", name: "Text" },
  {
    key: "_decode",
    name: "Decode",
    sortable: false,
    width: 80,
    isAction: true,
  },
];

export const getFixLogColumns = () =>
  BASE_COLUMNS.map((column) => ({ ...column }));

export const FIX_LOG_COLUMNS = getFixLogColumns();

const resolveOrderedColumns = (columns = [], sectionKey) => {
  if (!sectionKey || !Array.isArray(columns) || columns.length === 0) {
    return columns;
  }

  const savedOrder = getPreference(`columnOrder_${sectionKey}`);
  if (!Array.isArray(savedOrder) || savedOrder.length === 0) {
    return columns;
  }

  const orderedKeys = savedOrder
    .map((item) => (typeof item === "string" ? item : item?.key))
    .filter(Boolean);

  if (!orderedKeys.length) {
    return columns;
  }

  const columnByKey = new Map(columns.map((column) => [column.key, column]));
  const resolved = [];

  orderedKeys.forEach((key) => {
    const column = columnByKey.get(key);
    if (column) {
      resolved.push(column);
      columnByKey.delete(key);
    }
  });

  columnByKey.forEach((column) => resolved.push(column));
  return resolved;
};

const sortQuickFixRows = (rows = [], sortConfig = null) => {
  if (!Array.isArray(rows) || rows.length === 0 || !sortConfig?.key) {
    return rows;
  }

  const normalizeToUtc = (value) => {
    if (!value) return "";
    return String(value).endsWith("Z") ? String(value) : `${value}Z`;
  };

  const directionMultiplier = sortConfig.direction === "asc" ? 1 : -1;
  const sorted = [...rows];

  sorted.sort((firstRow, secondRow) => {
    const firstValue = firstRow?.[sortConfig.key];
    const secondValue = secondRow?.[sortConfig.key];

    if (sortConfig.key === "time") {
      const firstTime = Date.parse(normalizeToUtc(firstValue));
      const secondTime = Date.parse(normalizeToUtc(secondValue));
      return ((firstTime || 0) - (secondTime || 0)) * directionMultiplier;
    }

    const firstString = (firstValue ?? "").toString();
    const secondString = (secondValue ?? "").toString();
    const delta = firstString.localeCompare(secondString, undefined, { numeric: true });
    return delta * directionMultiplier;
  });

  return sorted;
};

const formatFilterDate = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DDMMMYY HH:mm:ss") : `${value}`;
};

const toStringArray = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value
      .map((entry) => (entry == null ? "" : `${entry}`.trim()))
      .filter(Boolean);
  }
  const normalized = `${value}`.trim();
  return normalized ? [normalized] : [];
};

const buildDownloadContext = (filters = {}) => {
  const downloadFilters = { ...filters };
  const hasDateFilter = Boolean(downloadFilters.timeFrom || downloadFilters.timeTo);

  const appliedFilters = [];
  if (hasDateFilter) {
    const fromValue = formatFilterDate(downloadFilters.timeFrom);
    const toValue = formatFilterDate(downloadFilters.timeTo);
    if (fromValue && toValue) {
      appliedFilters.push({ label: "Date Range", value: `${fromValue} to ${toValue} (UTC)` });
    } else if (fromValue) {
      appliedFilters.push({ label: "From", value: `${fromValue} (UTC)` });
    } else if (toValue) {
      appliedFilters.push({ label: "To", value: `${toValue} (UTC)` });
    }
  }

  const sender = downloadFilters.sendercompid ? `${downloadFilters.sendercompid}`.trim() : "";
  const target = downloadFilters.targetcompid ? `${downloadFilters.targetcompid}`.trim() : "";
  if (sender && target) {
    appliedFilters.push({ label: "Session", value: `${sender}-${target}` });
  } else if (sender) {
    appliedFilters.push({ label: "Sender CompID", value: sender });
  } else if (target) {
    appliedFilters.push({ label: "Target CompID", value: target });
  }

  const includes = toStringArray(downloadFilters.textIncludes);
  if (includes.length) {
    appliedFilters.push({ label: "Text Includes", value: includes.join(", ") });
  }

  const excludes = toStringArray(downloadFilters.textExcludes);
  if (excludes.length) {
    appliedFilters.push({ label: "Text Excludes", value: excludes.join(", ") });
  }

  return {
    hasDateFilter,
    downloadFilters,
    appliedFilters,
  };
};

/**
 * Download QuickFix logs as Excel or CSV
 * @param {string} token - Auth token
 * @param {Function} fetcher - API fetcher function (fetchQuickFixMessages or fetchQuickFixEvents)
 * @param {Object} filters - Current filter state
 * @param {boolean} hasActiveFilters - Whether filters are applied
 * @param {Array} selectedColumns - Columns to include in download
 * @param {string} format - 'xlsx' or 'csv'
 * @param {string} logType - 'messages' or 'events' for filename
 */
export const downloadFixLogs = async (
  token,
  fetcher,
  filters,
  _hasActiveFilters,
  selectedColumns,
  format = "xlsx",
  logType = "messages",
  options = {}
) => {
  const { sortConfig = null, sectionKey = null } = options;
  const context = buildDownloadContext(filters);

  try {
    let downloadSize = 100000; // Max limit for download

    // Fetch all data with filters
    const response = await fetcher(token, {
      page: 0,
      size: downloadSize,
      filters: context.downloadFilters,
    });

    if (!response || !response.content || response.content.length === 0) {
      return {
        status: "empty",
        context,
      };
    }

    const orderedColumns = resolveOrderedColumns(selectedColumns, sectionKey);
    // Filter out the _decode action column
    const exportColumns = orderedColumns.filter((col) => col.key !== "_decode");
    const exportRows = sortQuickFixRows(response.content, sortConfig);

    // Build 2D array: headers + rows
    const headers = exportColumns.map((col) => col.name);
    const rows = exportRows.map((row) =>
      exportColumns.map((col) => {
        let value = row[col.key];
        if (value === null || value === undefined) return "";
        // Format timestamp
        if (col.render && typeof col.render === 'function') {
          return col.render(value);
        }
        // Convert objects/arrays to JSON string
        if (typeof value === 'object') {
          try {
            return JSON.stringify(value);
          } catch (e) {
            return String(value);
          }
        }
        return value;
      })
    );

    const aoa = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'QuickFix Logs');

    let blob;
    let filename;
    if (format === "csv") {
      const csv = XLSX.utils.sheet_to_csv(ws);
      blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      filename = `quickfix_${logType}_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
    } else {
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      filename = `quickfix_${logType}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
    }

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    return {
      status: "success",
      count: response.content.length,
      context,
    };
  } catch (error) {
    console.error("Error downloading QuickFix logs:", error);

    return {
      status: "error",
      error,
      context,
    };
  }
};
